import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { validateEmailFormat } from "@vega-core/helpers/formik.helper";

import {
  SearchMultiselect,
  type SearchMultiselectProps,
} from "../SearchMultiSelect/SearchMultiselect";
import { type SearchMultiSelectItem } from "../SearchMultiSelect/types";

import { UserSearchSkeleton } from "./UserSearchSkeleton";

export type User = {
  id: string;
  fullName: string;
  email: string;
};

export type SearchMultiSelectItemWithUser = SearchMultiSelectItem & User;

export type SearchUserMultiselectProps<
  Multiple extends boolean | undefined = false,
> = SearchMultiselectProps<SearchMultiSelectItemWithUser, Multiple>;

/**
 * Utils for converting a User to a SearchMultiSelectItemWithUser
 */
export const addSelectItemFieldsToUser = (
  item: User,
): SearchMultiSelectItemWithUser => ({
  ...item,
  label: item.fullName,
  searchValue: `${item.fullName} ${item.email}`.toLowerCase(),
});

/**
 * Builds the items list for a user-multiselect that also accepts freeform
 * email entries. Keeps previously-selected freeform emails in the list
 * because base-ui's Combobox prunes selected values whose id is missing
 * from `items`. Appends a synthetic item for the currently-typed value
 * when it's a valid email not already present.
 */
export const getUserOrFreeformEmailItems = ({
  users,
  selectedItems,
  searchValue,
}: {
  users: SearchMultiSelectItemWithUser[];
  selectedItems: SearchMultiSelectItemWithUser[];
  searchValue: string;
}): SearchMultiSelectItemWithUser[] => {
  const selectedFreeform = selectedItems.filter(
    (s) => !users.some((u) => u.id === s.id),
  );
  const base = [...users, ...selectedFreeform];

  const trimmed = searchValue.trim();
  const isNewValidEmail =
    trimmed !== "" &&
    validateEmailFormat(trimmed) &&
    !base.some((u) => u.email.toLowerCase() === trimmed.toLowerCase());

  if (!isNewValidEmail) {
    return base;
  }

  return [
    ...base,
    addSelectItemFieldsToUser({
      id: crypto.randomUUID(),
      email: trimmed,
      fullName: trimmed,
    }),
  ];
};

const highlightText = (text: string, highlight: string) => {
  if (!highlight.trim()) {
    return text;
  }

  const regex = new RegExp(`(${highlight})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <span className="text-text/primary" key={index}>
        {part}
      </span>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <span className="text-text/secondary" key={index}>
        {part}
      </span>
    ),
  );
};

const renderUserItem = (
  item: SearchMultiSelectItemWithUser,
  inputValue: string,
) => {
  const { fullName, email } = item;

  return (
    <div className="flex items-center gap-2">
      <Avatar initials={fullName} />
      <div>
        <div className="font-medium" data-testid="searchUser-userItem-name">
          {highlightText(fullName, inputValue)}
        </div>
        <div
          className="body-small-regular text-text/secondary"
          data-testid="searchUser-userItem-email"
        >
          {highlightText(email, inputValue)}
        </div>
      </div>
    </div>
  );
};

/**
 * Similar to SearchUser, but uses SearchMultiselect as the underlying component.
 */
export const SearchUserMultiselect = <
  Multiple extends boolean | undefined = false,
>({
  ...props
}: SearchUserMultiselectProps<Multiple>) => (
  <SearchMultiselect
    renderItem={renderUserItem}
    renderLoader={() => <UserSearchSkeleton />}
    {...props}
  />
);
